import { useCallback } from "react";

import type { UseQueryResult } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { useCompanyDomainsQuery } from "../query/queries/useCompanyDomainsQuery";
import useDomainsDetailedResultsQueryWithDto from "../query/queries/useDomainsDetailedResultsQueryWithDto";
import { selectDomainSearchResultsDto } from "../store/selectors";
import type {
    EnrichedLeadsSearchRow,
    LeadDomainsSearchResultsDto,
    LeadDomainsSearchTableData,
    LeadDomainsTableCompanyModel,
    LeadsSearchColumnListConfig,
    LeadsSearchRow,
} from "../types";
import type { ColumnAdapterContext } from "../types/columns";
import { fromDtoToColumnsConfig } from "../utils";
import { transformRowDto } from "../utils/rows";

import { useEnrichedTableData } from "./useEnrichedTableData";

type HookValue = Pick<UseQueryResult<LeadDomainsSearchTableData<EnrichedLeadsSearchRow>>, "data" | "isFetching">;

const useFetchDomainsTableResults = (
    domainsCompanyModel: LeadDomainsTableCompanyModel,
    columnsConfig: LeadsSearchColumnListConfig,
): HookValue => {
    const requestDto = useSelector(selectDomainSearchResultsDto);

    const transformCompanyDomainsData = useCallback(
        (data: LeadDomainsSearchResultsDto): LeadDomainsSearchTableData<LeadsSearchRow> => ({
            ...data,
            rows: transformRowDto(data.rows),
            columns: fromDtoToColumnsConfig<LeadsSearchRow, ColumnAdapterContext>(data.columns, columnsConfig),
        }),
        [columnsConfig],
    );
    const companyDomainsQueryResult = useCompanyDomainsQuery(
        {
            domain: domainsCompanyModel.companyDomain,
            dto: requestDto,
        },
        transformCompanyDomainsData,
    );

    const domainsDetailedResultsQueryResult = useDomainsDetailedResultsQueryWithDto(
        {
            leadsType: "websites",
            dto: requestDto,
            rows: companyDomainsQueryResult.data?.rows ?? [],
        },
        companyDomainsQueryResult.isSuccess,
    );

    const enrichedData = useEnrichedTableData<
        EnrichedLeadsSearchRow,
        LeadDomainsSearchTableData<EnrichedLeadsSearchRow>
    >(companyDomainsQueryResult.data, domainsDetailedResultsQueryResult.data);

    return {
        isFetching: companyDomainsQueryResult.isFetching || domainsDetailedResultsQueryResult.isFetching,
        data: enrichedData,
    };
};

export default useFetchDomainsTableResults;
